import { TimingMode } from "../../../entities/timing-mode";
import { TimingState } from "../../../entities/timing-state";
import { IComponentSource, ComponentModel, compressComponentSource, MarkdownComponent, timingSpecPattern, formulaicTimingSpecPattern } from "./core";
import { applyTextDecoration } from "./text-decoration";

export interface ITimingSpecComponentModel extends IComponentSource {
  specType: TimingMode;
  primaryUnits: number;
  subUnits?: number;
  title: string;
  notes: ComponentModel[];
  hideNotes: boolean;
  timingState: TimingState;
  computedPrimaryUnits?: number;
  computedSubUnits?: number;
}

export class TimingSpecComponentModel extends ComponentModel implements ITimingSpecComponentModel {
  public timingState: TimingState = TimingState.Idle;
  public computedPrimaryUnits?: number | undefined = undefined;
  public computedSubUnits?: number | undefined = undefined;
  public specType: TimingMode = TimingMode.Preamble;
  public primaryUnits: number = 0;
  public subUnits?: number | undefined = undefined;
  public title: string = '';
  public notes: ComponentModel[] = [];
  public hideNotes: boolean = false;
  public computedDuration: number | undefined = undefined;
}

const completeStandardTimingSpecComponentModel = (header: string, timingComponent: TimingSpecComponentModel) => {
  const timeSpec = header.match(timingSpecPattern)![0].trim();
  if(timeSpec.indexOf(':') < 0) {
    timingComponent.specType = TimingMode.ShareTime;
  } else if(timeSpec[0] === '#') {
    timingComponent.specType = TimingMode.FirmTime;
  } else {
    timingComponent.specType = TimingMode.FlexTime;
  }

  const units = timeSpec.substring(1);
  if(timingComponent.specType === TimingMode.ShareTime) {
    timingComponent.primaryUnits = parseInt(units);
  } else {
    const unitParts = `0${units}`.split(':');
    timingComponent.primaryUnits = parseInt(unitParts[0]);
    timingComponent.subUnits = parseInt(unitParts[1]);
  }

  const title = header.replace(timingSpecPattern, '');
  timingComponent.title = applyTextDecoration(MarkdownComponent.TimingSpec, title);
  
  return timingComponent;
}

const getTimingSpecScalarValue = (value: string) => {
  if(value.includes(':')) {
    const unitParts = `0${value}`.split(':');
    const mins = parseInt(unitParts[0]);
    const secs = parseInt(unitParts[1]);
    const result = (mins * 60) + secs;
    return result;
  } else {
    return parseInt(value);
  }
}

const completeFormulaicTimingSpecComponentModel = (header: string, timingComponent: TimingSpecComponentModel) => {
  const match = header.match(formulaicTimingSpecPattern)!;
  const fullSpec = match[0].trim();
  const leftSpec = match[1].trim();
  const operator = match[2];
  const rightSpec = match[3].trim();
  if(leftSpec.indexOf(':') < 0 && rightSpec.indexOf(':') < 0) {
    timingComponent.specType = TimingMode.ShareTime;
  } else if(fullSpec[0] === '#') {
    timingComponent.specType = TimingMode.FirmTime;
  } else {
    timingComponent.specType = TimingMode.FlexTime;
  }

  const leftValue = getTimingSpecScalarValue(leftSpec);
  const rightValue = getTimingSpecScalarValue(rightSpec);
  let scalarResult = 0;
  switch(operator) {
    case '+':
      scalarResult = leftValue + rightValue;
      break;
    case '-':
      scalarResult = Math.abs(leftValue - rightValue);
      break;
    case '*':
      scalarResult = leftValue * rightValue;
      break;
    case '/':
      scalarResult = rightValue > 0 ? Math.round(leftValue / rightValue) : leftValue;
      break;
    case '%':
      scalarResult = rightValue > 0 ? leftValue % rightValue : leftValue;
      break;
  }

  if(timingComponent.specType === TimingMode.ShareTime) {
    timingComponent.primaryUnits = scalarResult;
  } else {
    timingComponent.primaryUnits = Math.floor(scalarResult / 60);
    timingComponent.subUnits = scalarResult % 60;
  }

  const title = header.replace(formulaicTimingSpecPattern, '');
  timingComponent.title = applyTextDecoration(MarkdownComponent.TimingSpec, title);
  
  return timingComponent;
}

export const buildTimingSpecComponentModel = (source: IComponentSource) => {
  compressComponentSource(source, false);
  const timingComponent = new TimingSpecComponentModel(source);
  const content = source.content[0].content;

  if(formulaicTimingSpecPattern.test(content)) {
    return completeFormulaicTimingSpecComponentModel(content, timingComponent);
  } 
  
  return completeStandardTimingSpecComponentModel(content, timingComponent);
}